import QRCode from 'qrcode'
import JSZip from 'jszip'

/** 单次最多导出的学生人数 */
export const MAX_STUDENT_QR_COUNT = 300

export const QR_SIZE = 200

const QR_CHUNK_SIZE = 20

/**
 * 清理文件名非法字符
 */
export function sanitizeFileName(name) {
  const text = String(name || '').replace(/[\\/:*?"<>|]/g, '_').trim()
  return text || '未命名'
}

/**
 * 归一化学生行数据
 */
export function normalizeStudent(row) {
  return {
    id: row.Id != null ? row.Id : row.id,
    stuCode: (row.StuCode || row.stuCode || '').trim(),
    stuName: row.StuName || row.stuName || '',
    departmentName: row.DepartmentName || row.departmentName || '未分班'
  }
}

/** 二维码类型：学号 */
export const QR_TYPE_STU_CODE = 'StuCode'
/** 二维码类型：学生ID */
export const QR_TYPE_STUDENT_ID = 'StudentId'

/**
 * 获取二维码类型显示名
 */
export function getQrTypeLabel(qrType) {
  return qrType === QR_TYPE_STUDENT_ID ? '学生ID' : '学号'
}

/**
 * 获取二维码编码内容
 */
export function getQrContent(student, qrType) {
  if (qrType === QR_TYPE_STUDENT_ID) {
    return String(student.id)
  }
  return student.stuCode || ''
}

/**
 * 当前类型是否可生成二维码
 */
export function canGenerateQr(student, qrType) {
  if (qrType === QR_TYPE_STUDENT_ID) {
    return student.id != null && student.id !== ''
  }
  return !!(student.stuCode && String(student.stuCode).trim())
}

/**
 * 统计预计二维码张数
 */
export function countQrImages(students, qrType) {
  const type = qrType || QR_TYPE_STU_CODE
  let count = 0
  ;(students || []).forEach(function (row) {
    const s = row.id != null && row.stuName != null ? row : normalizeStudent(row)
    if (canGenerateQr(s, type)) {
      count += 1
    }
  })
  return count
}

/**
 * 构建 PNG 文件名
 */
export function buildQrFileName(student, qrType) {
  const dept = sanitizeFileName(student.departmentName)
  const name = sanitizeFileName(student.stuName)
  if (qrType === QR_TYPE_STUDENT_ID) {
    return dept + '_' + name + '_学生ID_' + student.id + '.png'
  }
  return dept + '_' + name + '_学号_' + student.stuCode + '.png'
}

/**
 * 生成二维码 DataURL（预览用）
 */
export function generateQrDataUrl(content) {
  return QRCode.toDataURL(String(content), {
    width: QR_SIZE,
    margin: 1,
    errorCorrectionLevel: 'M'
  })
}

/**
 * 生成二维码 PNG Blob（导出用）
 */
export function generateQrBlob(content) {
  const canvas = document.createElement('canvas')
  return QRCode.toCanvas(canvas, String(content), {
    width: QR_SIZE,
    margin: 1,
    errorCorrectionLevel: 'M'
  }).then(function () {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) {
          resolve(blob)
        } else {
          reject(new Error('生成 PNG 失败'))
        }
      }, 'image/png')
    })
  })
}

/**
 * 格式化 ZIP 文件名时间戳
 */
export function formatZipTimeStamp() {
  const d = new Date()
  const p = function (n) {
    return n < 10 ? '0' + n : '' + n
  }
  return (
    d.getFullYear() +
    p(d.getMonth() + 1) +
    p(d.getDate()) +
    p(d.getHours()) +
    p(d.getMinutes()) +
    p(d.getSeconds())
  )
}

/**
 * 构建 ZIP 根目录名
 */
export function buildZipFolderName(qrType) {
  return '学生二维码_' + getQrTypeLabel(qrType || QR_TYPE_STU_CODE) + '_' + formatZipTimeStamp()
}

/**
 * 批量导出学生二维码 ZIP（每位学生 1 张，按选定类型）
 */
export async function exportStudentsQrZip(students, qrType, onProgress) {
  const type = qrType || QR_TYPE_STU_CODE
  const zip = new JSZip()
  const folderName = buildZipFolderName(type)
  const folder = zip.folder(folderName)
  const normalized = (students || []).map(normalizeStudent)
  const exportable = normalized.filter(function (s) {
    return canGenerateQr(s, type)
  })
  let done = 0
  const total = exportable.length

  for (let i = 0; i < exportable.length; i++) {
    const student = exportable[i]
    const content = getQrContent(student, type)
    const blob = await generateQrBlob(content)
    folder.file(buildQrFileName(student, type), blob)
    done += 1
    if (onProgress) {
      onProgress(done, total, student.stuName)
    }

    if ((i + 1) % QR_CHUNK_SIZE === 0) {
      await new Promise(function (resolve) {
        setTimeout(resolve, 0)
      })
    }
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  return {
    blob: zipBlob,
    folderName: folderName,
    fileCount: total,
    skippedCount: normalized.length - exportable.length
  }
}

/**
 * 触发浏览器下载 Blob
 */
export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * 超出人数上限时的提示文案
 */
export function buildOverLimitMessage(count) {
  return (
    '当前共 ' +
    count +
    ' 人，超过上限 ' +
    MAX_STUDENT_QR_COUNT +
    ' 人。请在左侧切换到具体年级或班级后再操作。'
  )
}
